import { useGLTF } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import {
  Component,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import * as THREE from "three";
import type { RoomLayoutRuntime } from "../../shared/types";
import {
  getDoorExitCell,
  getTileHeight,
  getTileSurface,
  getWalkableCells,
  getWalkableSet,
  TILE_WORLD_HEIGHT_UNIT,
} from "../../shared/room-layout";
import {
  resolveEvenFootprintRenderOffset,
  resolveFurnitureFootprint,
  resolveFurnitureFootprintCellsAt,
  resolveFurnitureFootprintCellsForAsset,
} from "../../shared/furniture-footprint";
import {
  FRAME_ROTATION_NORTH,
  clampFrameDimensions,
  frameDirectionFromRotation,
  frameRotationFromDirection,
  frameSlotToWorldTransform,
  resolveFrameWallRuns,
} from "../../shared/frame-placement";
import type { SelectedAsset } from "../hooks/useMarketplace";
import { normalizeGlb } from "../lib/glb-normalize";
import type { RoomAsset } from "../../shared/types";

const OUTLINE_ORANGE = new THREE.Color(0xff922b);
const OUTLINE_GREEN = new THREE.Color(0x40c057);
const OUTLINE_RED = new THREE.Color(0xfa5252);
const FRAME_EDGE_MARGIN = 0.03;
const STACK_EPSILON = 0.0001;

function resolveAssetStackHeight(asset: Pick<RoomAsset, "stackHeight">): number {
  const raw = typeof asset.stackHeight === "number" ? asset.stackHeight : 1;
  return THREE.MathUtils.clamp(raw, 0, 8);
}

function resolveAssetTopY(asset: Pick<RoomAsset, "y" | "stackHeight">): number {
  return (asset.y ?? 0) + resolveAssetStackHeight(asset);
}

function footprintsOverlapWithProbe(
  probeCells: Array<[number, number]>,
  candidate: Pick<RoomAsset, "x" | "z" | "blockWidth" | "blockDepth" | "rotation">
): boolean {
  const candidateKeys = new Set(
    resolveFurnitureFootprintCellsForAsset(candidate).map(
      ([x, z]) => `${x},${z}`
    )
  );
  for (const [x, z] of probeCells) {
    if (candidateKeys.has(`${x},${z}`)) {
      return true;
    }
  }
  return false;
}

interface GlbStackProbe {
  x: number;
  y: number;
  z: number;
  blockWidth: number;
  blockDepth: number;
}

function resolveAutoStackYForProbe(
  probe: GlbStackProbe,
  candidates: RoomAsset[]
): number {
  const probeCells = resolveFurnitureFootprintCellsAt(
    probe.x,
    probe.z,
    Math.max(1, probe.blockWidth),
    Math.max(1, probe.blockDepth)
  );
  let y = 0;
  for (const candidate of candidates) {
    if (
      !footprintsOverlapWithProbe(probeCells, candidate) ||
      !candidate.stackableTop
    ) {
      continue;
    }
    y = Math.max(y, resolveAssetTopY(candidate));
  }
  return THREE.MathUtils.clamp(Math.round(y * 1000) / 1000, 0, 8);
}

function findStackPlacementBlocker(
  probe: GlbStackProbe,
  targetY: number,
  candidates: RoomAsset[]
): RoomAsset | null {
  const probeCells = resolveFurnitureFootprintCellsAt(
    probe.x,
    probe.z,
    Math.max(1, probe.blockWidth),
    Math.max(1, probe.blockDepth)
  );
  for (const candidate of candidates) {
    if (!footprintsOverlapWithProbe(probeCells, candidate)) {
      continue;
    }
    const candidateY = candidate.y ?? 0;
    const candidateTopY = resolveAssetTopY(candidate);
    if (Math.abs(candidateY - targetY) <= STACK_EPSILON) {
      return candidate;
    }
    if (targetY < candidateTopY - STACK_EPSILON) {
      return candidate;
    }
    if (targetY > candidateY + STACK_EPSILON && !candidate.stackableTop) {
      return candidate;
    }
  }
  return null;
}

/** Error boundary that catches GLB loading/parsing failures and renders a fallback. */
class GlbErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: unknown) {
    console.warn(
      "[PlacementGhost] GLB load failed, using fallback box:",
      error
    );
  }
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

type PlacementPhase = "placing" | "confirming";

interface Props {
  layout: RoomLayoutRuntime;
  asset: SelectedAsset;
  placedAssets: RoomAsset[];
  rotation: number;
  phase: PlacementPhase;
  confirmedPos: { x: number; y: number; z: number; rotation?: number } | null;
  movingAssetId?: string | null;
  onLockPosition: (placement: {
    x: number;
    y: number;
    z: number;
    rotation?: number;
  }) => void;
}

function GhostGlbModel({
  url,
  outlineColor,
}: {
  url: string;
  outlineColor: THREE.Color;
}) {
  const gltf = useGLTF(url) as { scene: THREE.Group };

  const { edges, scale, offset } = useMemo(() => {
    const cloned = gltf.scene.clone(true);
    const { scale: s, offset: o } = normalizeGlb(cloned);

    // Render edge-only ghost to avoid floor tinting and lighting shifts while placing.
    const edgeLines: THREE.LineSegments[] = [];
    cloned.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        const edgeGeo = new THREE.EdgesGeometry(mesh.geometry, 30);
        const line = new THREE.LineSegments(
          edgeGeo,
          new THREE.LineBasicMaterial({
            color: OUTLINE_ORANGE,
            linewidth: 1,
            toneMapped: false,
          })
        );
        line.position.copy(mesh.position);
        line.rotation.copy(mesh.rotation);
        line.scale.copy(mesh.scale);
        // Apply parent transforms
        mesh.parent?.updateMatrixWorld(true);
        if (mesh.parent && mesh.parent !== cloned) {
          line.applyMatrix4(mesh.parent.matrixWorld);
        }
        edgeLines.push(line);
      }
    });

    return { edges: edgeLines, scale: s, offset: o };
  }, [gltf.scene]);

  useEffect(() => {
    edges.forEach((line) => {
      (line.material as THREE.LineBasicMaterial).color.copy(outlineColor);
    });
  }, [edges, outlineColor]);

  return (
    <group position={[offset.x, offset.y, offset.z]}>
      <group scale={[scale, scale, scale]}>
        {edges.map((line, i) => (
          <primitive key={i} object={line} />
        ))}
      </group>
    </group>
  );
}

function FallbackBox({ outlineColor }: { outlineColor: THREE.Color }) {
  const groupRef = useRef<THREE.Group>(null);
  const elapsed = useRef(0);

  useFrame((_, delta) => {
    elapsed.current += delta;
    if (groupRef.current) {
      groupRef.current.rotation.y = elapsed.current * 0.8;
      groupRef.current.position.y = 0.4 + Math.sin(elapsed.current * 2) * 0.06;
    }
  });

  return (
    <group ref={groupRef} position={[0, 0.4, 0]}>
      <mesh>
        <octahedronGeometry args={[0.3, 0]} />
        <meshBasicMaterial
          color={outlineColor}
          transparent
          opacity={0.34}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

function GhostFrame({
  borderColor,
  width,
  height,
}: {
  borderColor: string;
  width: number;
  height: number;
}) {
  const border = THREE.MathUtils.clamp(Math.min(width, height) * 0.09, 0.045, 0.1);
  const depth = 0.05;
  const innerWidth = Math.max(0.08, width - border * 2);
  const innerHeight = Math.max(0.08, height - border * 2);
  const horizontalOffset = (height - border) / 2;
  const verticalOffset = (width - border) / 2;

  return (
    <group>
      <mesh position={[0, horizontalOffset, 0]}>
        <boxGeometry args={[width, border, depth]} />
        <meshBasicMaterial
          color={borderColor}
          transparent
          opacity={0.42}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
      <mesh position={[0, -horizontalOffset, 0]}>
        <boxGeometry args={[width, border, depth]} />
        <meshBasicMaterial
          color={borderColor}
          transparent
          opacity={0.42}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
      <mesh position={[-verticalOffset, 0, 0]}>
        <boxGeometry args={[border, innerHeight, depth]} />
        <meshBasicMaterial
          color={borderColor}
          transparent
          opacity={0.42}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
      <mesh position={[verticalOffset, 0, 0]}>
        <boxGeometry args={[border, innerHeight, depth]} />
        <meshBasicMaterial
          color={borderColor}
          transparent
          opacity={0.42}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
      <mesh position={[0, 0, depth / 2 + 0.001]}>
        <planeGeometry args={[Math.max(0.06, innerWidth - 0.04), Math.max(0.06, innerHeight - 0.04)]} />
        <meshBasicMaterial
          color="#ffffff"
          transparent
          opacity={0.2}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

function GhostDirectionArrow({
  color,
  y,
  scale,
}: {
  color: THREE.Color;
  y: number;
  scale: number;
}) {
  return (
    <group position={[0, y, 0]} scale={[scale, scale, scale]}>
      <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.026, 0.026, 0.46, 14]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.92}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
      <mesh position={[0, 0, 0.33]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.082, 0.22, 14]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.96}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
      <mesh position={[0, 0, -0.26]}>
        <sphereGeometry args={[0.04, 12, 12]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.9}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

export function PlacementGhost({
  layout,
  asset,
  placedAssets,
  rotation,
  phase,
  confirmedPos,
  movingAssetId,
  onLockPosition,
}: Props) {
  const walkableSet = useMemo(() => getWalkableSet(layout), [layout]);
  const walkableCells = useMemo(() => getWalkableCells(layout), [layout]);
  const [exitTileX, exitTileZ] = useMemo(() => getDoorExitCell(layout), [layout]);
  const existingGlbAssets = useMemo(
    () =>
      placedAssets.filter(
        (candidate) => candidate.type === "glb" && candidate.id !== movingAssetId
      ),
    [placedAssets, movingAssetId]
  );
  const frameWallRuns = useMemo(() => resolveFrameWallRuns(layout), [layout]);
  const frameDimensions = useMemo(
    () =>
      asset.type === "frame"
        ? clampFrameDimensions(asset.frameWidth, asset.frameHeight)
        : null,
    [asset.type, asset.frameWidth, asset.frameHeight]
  );
  const [cursorPos, setCursorPos] = useState<{
    x: number;
    y: number;
    z: number;
    rotation?: number;
  }>({
    x: 0,
    y: 0,
    z: 0,
  });
  const [hasPlacementTarget, setHasPlacementTarget] = useState(false);
  const [frameSlotAvailable, setFrameSlotAvailable] = useState(false);
  const [glbSlotAvailable, setGlbSlotAvailable] = useState(false);
  const { camera, raycaster, pointer } = useThree();

  const pickPlacementCellFromRay = (
    ray: THREE.Ray
  ): { x: number; z: number } | null => {
    if (Math.abs(ray.direction.y) < 0.000001) {
      return null;
    }
    let bestT = Number.POSITIVE_INFINITY;
    let best: { x: number; z: number } | null = null;
    const tryCellPlane = (x: number, z: number, planeY: number) => {
      const t = (planeY - ray.origin.y) / ray.direction.y;
      if (t <= 0 || t >= bestT) {
        return;
      }
      const hitX = ray.origin.x + ray.direction.x * t;
      const hitZ = ray.origin.z + ray.direction.z * t;
      if (
        hitX < x - 0.5 ||
        hitX > x + 0.5 ||
        hitZ < z - 0.5 ||
        hitZ > z + 0.5
      ) {
        return;
      }
      bestT = t;
      best = { x, z };
    };
    for (const [x, z] of walkableCells) {
      const tileY =
        getTileHeight(layout, x, z) * TILE_WORLD_HEIGHT_UNIT + 0.016;
      tryCellPlane(x, z, tileY);
    }
    for (const candidate of existingGlbAssets) {
      if (!candidate.stackableTop) {
        continue;
      }
      const stackTopDeltaY = resolveAssetTopY(candidate) - (candidate.y ?? 0);
      for (const [cellX, cellZ] of resolveFurnitureFootprintCellsForAsset(candidate)) {
        if (!walkableSet.has(`${cellX},${cellZ}`)) {
          continue;
        }
        if (getTileSurface(layout, cellX, cellZ) === "stairs") {
          continue;
        }
        const topY =
          getTileHeight(layout, cellX, cellZ) * TILE_WORLD_HEIGHT_UNIT +
          (candidate.y ?? 0) +
          stackTopDeltaY +
          0.016;
        tryCellPlane(cellX, cellZ, topY);
      }
    }
    return best;
  };

  const pickFrameWallPlacementFromRay = (
    ray: THREE.Ray,
    frameWidth: number,
    frameHeight: number
  ): { x: number; y: number; z: number; rotation: number } | null => {
    if (frameWallRuns.length === 0) {
      return null;
    }

    const halfWidth = frameWidth / 2;
    const halfHeight = frameHeight / 2;
    let best:
      | {
          x: number;
          y: number;
          z: number;
          rotation: number;
          t: number;
        }
      | null = null;

    for (const run of frameWallRuns) {
      const direction = run.direction;
      const denom = direction === "north" ? ray.direction.z : ray.direction.x;
      if (Math.abs(denom) < 0.000001) {
        continue;
      }

      const originAxis = direction === "north" ? ray.origin.z : ray.origin.x;
      const t = (run.plane - originAxis) / denom;
      if (t <= 0 || !Number.isFinite(t)) {
        continue;
      }

      const hitX = ray.origin.x + ray.direction.x * t;
      const hitY = ray.origin.y + ray.direction.y * t;
      const hitZ = ray.origin.z + ray.direction.z * t;
      const alongRaw = direction === "north" ? hitX : hitZ;
      const minAlong = run.alongMin + halfWidth + FRAME_EDGE_MARGIN;
      const maxAlong = run.alongMax - halfWidth - FRAME_EDGE_MARGIN;
      if (maxAlong < minAlong) {
        continue;
      }
      if (alongRaw < minAlong - 0.2 || alongRaw > maxAlong + 0.2) {
        continue;
      }

      const along = THREE.MathUtils.clamp(alongRaw, minAlong, maxAlong);
      const coverageMin = along - halfWidth - FRAME_EDGE_MARGIN;
      const coverageMax = along + halfWidth + FRAME_EDGE_MARGIN;
      const coveredSegments = run.segments.filter(
        (segment) =>
          segment.alongMax > coverageMin && segment.alongMin < coverageMax
      );
      if (coveredSegments.length === 0) {
        continue;
      }

      const baseY = coveredSegments.reduce(
        (highest, segment) => Math.max(highest, segment.baseY),
        Number.NEGATIVE_INFINITY
      );
      const minY = baseY + halfHeight + FRAME_EDGE_MARGIN;
      const maxY = run.topY - halfHeight - FRAME_EDGE_MARGIN;
      if (!Number.isFinite(minY) || !Number.isFinite(maxY) || maxY < minY) {
        continue;
      }
      if (hitY < minY - 0.35 || hitY > maxY + 0.35) {
        continue;
      }

      const y = THREE.MathUtils.clamp(hitY, minY, maxY);
      const x = direction === "north" ? along : run.plane;
      const z = direction === "north" ? run.plane : along;
      if (!best || t < best.t) {
        best = {
          x,
          y,
          z,
          rotation: frameRotationFromDirection(direction),
          t,
        };
      }
    }

    if (!best) {
      return null;
    }
    return {
      x: best.x,
      y: best.y,
      z: best.z,
      rotation: best.rotation,
    };
  };

  // Track cursor in placing mode
  useFrame(() => {
    if (phase !== "placing") return;
    raycaster.setFromCamera(pointer, camera);
    if (asset.type === "frame") {
      const dims = frameDimensions ?? clampFrameDimensions(undefined, undefined);
      const frameHit = pickFrameWallPlacementFromRay(
        raycaster.ray,
        dims.width,
        dims.height
      );
      if (!frameHit) {
        setHasPlacementTarget((prev) => (prev ? false : prev));
        setFrameSlotAvailable((prev) => (prev ? false : prev));
        return;
      }
      setHasPlacementTarget((prev) => (prev ? prev : true));
      setCursorPos((prev) =>
        Math.abs(prev.x - frameHit.x) < 0.0001 &&
        Math.abs(prev.y - frameHit.y) < 0.0001 &&
        Math.abs(prev.z - frameHit.z) < 0.0001 &&
        prev.rotation === frameHit.rotation
          ? prev
          : frameHit
      );
      setFrameSlotAvailable((prev) => (prev ? prev : true));
      return;
    }

    setFrameSlotAvailable((prev) => (prev ? false : prev));
    const tileHit = pickPlacementCellFromRay(raycaster.ray);
    if (!tileHit) {
      setHasPlacementTarget((prev) => (prev ? false : prev));
      setGlbSlotAvailable((prev) => (prev ? false : prev));
      return;
    }
    const { x, z } = tileHit;
    if (!walkableSet.has(`${x},${z}`)) {
      setHasPlacementTarget((prev) => (prev ? false : prev));
      setGlbSlotAvailable((prev) => (prev ? false : prev));
      return;
    }
    const footprint = resolveFurnitureFootprint(
      asset.blockWidth,
      asset.blockDepth,
      rotation
    );
    const cells = resolveFurnitureFootprintCellsAt(
      x,
      z,
      footprint.width,
      footprint.depth
    );
    const footprintAllowed = cells.every(([cellX, cellZ]) => {
      if (!walkableSet.has(`${cellX},${cellZ}`)) {
        return false;
      }
      if (getTileSurface(layout, cellX, cellZ) === "stairs") {
        return false;
      }
      if (
        (cellX === layout.door.x && cellZ === layout.door.z) ||
        (cellX === exitTileX && cellZ === exitTileZ)
      ) {
        return false;
      }
      return true;
    });
    const probe: GlbStackProbe = {
      x,
      y: 0,
      z,
      blockWidth: footprint.width,
      blockDepth: footprint.depth,
    };
    const autoY = resolveAutoStackYForProbe(probe, existingGlbAssets);
    probe.y = autoY;
    const blockedByAsset = footprintAllowed
      ? findStackPlacementBlocker(probe, autoY, existingGlbAssets)
      : null;
    const available = footprintAllowed && blockedByAsset == null;
    setHasPlacementTarget((prev) => (prev ? prev : true));
    setCursorPos((prev) =>
      prev.x === x && Math.abs(prev.y - autoY) <= STACK_EPSILON && prev.z === z
        ? prev
        : { x, y: autoY, z }
    );
    setGlbSlotAvailable((prev) => (prev === available ? prev : available));
  });

  // Preload GLB
  useEffect(() => {
    if (asset.type === "glb" && asset.url) {
      useGLTF.preload(asset.url);
    }
  }, [asset.type, asset.url]);

  const placementAvailable =
    asset.type === "frame" ? frameSlotAvailable : glbSlotAvailable;
  const ghostOutlineColor =
    phase === "confirming"
      ? OUTLINE_GREEN
      : placementAvailable
        ? OUTLINE_ORANGE
        : OUTLINE_RED;
  const ghostOutlineCss = `#${ghostOutlineColor.getHexString()}`;

  const pos = phase === "confirming" && confirmedPos ? confirmedPos : cursorPos;
  const normalizedFrameRotation = frameDirectionFromRotation(
    pos.rotation ?? NaN
  )
    ? frameRotationFromDirection(
        frameDirectionFromRotation(pos.rotation ?? NaN) ?? "north"
      )
    : FRAME_ROTATION_NORTH;
  const frameTransform =
    asset.type === "frame"
      ? frameSlotToWorldTransform(
          {
            x: pos.x,
            y: pos.y,
            z: pos.z,
            rotation: normalizedFrameRotation,
          },
          getTileHeight(layout, Math.round(pos.x), Math.round(pos.z))
        )
      : null;
  const glbFootprint = useMemo(() => {
    if (asset.type !== "glb") {
      return null;
    }
    const footprint = resolveFurnitureFootprint(
      asset.blockWidth,
      asset.blockDepth,
      rotation
    );
    const maxSpan = Math.max(footprint.width, footprint.depth);
    const stackHeight = THREE.MathUtils.clamp(
      typeof asset.stackHeight === "number" ? asset.stackHeight : 1,
      0,
      8
    );
    return {
      cells: resolveFurnitureFootprintCellsAt(
        pos.x,
        pos.z,
        footprint.width,
        footprint.depth
      ),
      renderOffset: resolveEvenFootprintRenderOffset(
        footprint.width,
        footprint.depth
      ),
      directionArrowLift: Math.max(1.1, stackHeight + 0.35),
      directionArrowScale: Math.max(1, maxSpan * 0.78),
    };
  }, [
    asset.type,
    asset.blockWidth,
    asset.blockDepth,
    asset.stackHeight,
    rotation,
    pos.x,
    pos.z,
  ]);
  const glbFootprintCells = glbFootprint?.cells ?? [];
  const glbRenderAlignmentOffset = glbFootprint?.renderOffset ?? { x: 0, z: 0 };
  const glbDirectionArrowLift = glbFootprint?.directionArrowLift ?? 1.1;
  const glbDirectionArrowScale = glbFootprint?.directionArrowScale ?? 1;

  if (phase === "placing" && !hasPlacementTarget) {
    return null;
  }

  const rotY =
    asset.type === "frame" && frameTransform
      ? frameTransform.rotationY
      : (rotation * Math.PI) / 180;
  const glbFloorOffsetY =
    getTileHeight(layout, pos.x, pos.z) * TILE_WORLD_HEIGHT_UNIT + pos.y;

  const handleGhostClick = (e: { stopPropagation: () => void }) => {
    if (phase !== "placing") {
      return;
    }
    if (!placementAvailable) {
      return;
    }
    e.stopPropagation();
    if (asset.type === "frame") {
      onLockPosition({
        x: pos.x,
        y: pos.y,
        z: pos.z,
        rotation: pos.rotation ?? FRAME_ROTATION_NORTH,
      });
    } else {
      onLockPosition({ x: pos.x, y: pos.y, z: pos.z });
    }
  };

  if (asset.type === "frame") {
    return (
      <group
        position={
          frameTransform
            ? [frameTransform.x, frameTransform.y, frameTransform.z]
            : [pos.x, glbFloorOffsetY, pos.z]
        }
        rotation={[0, rotY, 0]}
        onClick={handleGhostClick}
      >
        <GhostFrame
          borderColor={ghostOutlineCss}
          width={frameDimensions?.width ?? 0.98}
          height={frameDimensions?.height ?? 0.98}
        />
      </group>
    );
  }

  return (
    <>
      {glbFootprintCells.map(([cellX, cellZ]) => (
        <mesh
          key={`footprint-${cellX}-${cellZ}`}
          position={[
            cellX,
            getTileHeight(layout, cellX, cellZ) * TILE_WORLD_HEIGHT_UNIT + pos.y + 0.02,
            cellZ,
          ]}
          rotation={[-Math.PI / 2, 0, 0]}
          onClick={handleGhostClick}
        >
          <planeGeometry args={[0.9, 0.9]} />
          <meshBasicMaterial
            color={ghostOutlineColor}
            transparent
            opacity={placementAvailable ? 0.14 : 0.2}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      ))}
      <group
        position={[
          pos.x + glbRenderAlignmentOffset.x,
          glbFloorOffsetY,
          pos.z + glbRenderAlignmentOffset.z,
        ]}
        rotation={[0, rotY, 0]}
        onClick={handleGhostClick}
      >
        <GlbErrorBoundary
          fallback={<FallbackBox outlineColor={ghostOutlineColor} />}
        >
          <Suspense fallback={<FallbackBox outlineColor={ghostOutlineColor} />}>
            <GhostGlbModel url={asset.url} outlineColor={ghostOutlineColor} />
          </Suspense>
        </GlbErrorBoundary>
        <GhostDirectionArrow
          color={ghostOutlineColor}
          y={glbDirectionArrowLift}
          scale={glbDirectionArrowScale}
        />
      </group>
    </>
  );
}
